"use client";

import { useState, useCallback } from "react";
import imageCompression from "browser-image-compression";
import { useAuth } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { useApiError } from "./useApiError";
import { config } from "@/config/env";
import { useToast } from "@/components/ui/use-toast";
import type { FrontendEffect } from "@/api/types";

export interface UploadedImage {
  id: string;
  sharedId: string; // ID to link the same logical image across different formats
  url: string;
  effect: FrontendEffect;
  format: VideoFormat;
  mediaType: "image" | "video";
  isTemp?: boolean;
}

export type VideoFormat = "16:9" | "9:16";

interface UseImageUploadOptions {
  maxImages?: number;
  defaultEffect?: FrontendEffect;
}

export type FormatImages = {
  "16:9": UploadedImage[];
  "9:16": UploadedImage[];
};

/**
 * Hook for managing image upload flow for multiple formats.
 * Uploads files through backend proxy to cloud storage.
 * Images are compressed and uploaded separately for each format.
 */
export function useImageUpload(options: UseImageUploadOptions = {}) {
  const { maxImages = 12, defaultEffect = "zoom-in" } = options;

  const { getToken } = useAuth();
  const { handleError } = useApiError();
  const { t } = useTranslation();
  const { toast } = useToast();

  // Store images per format
  const [imagesByFormat, setImagesByFormat] = useState<FormatImages>({
    "16:9": [],
    "9:16": [],
  });
  
  // Store original files to allow backfilling new formats
  const [originalFilesBySharedId, setOriginalFilesBySharedId] = useState<Record<string, { file: File, name: string }>>({});

  const [uploadingCount, setUploadingCount] = useState(0);
  const isUploading = uploadingCount > 0;
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  // Get all unique images across all formats (deduped by sharedId).
  // Using a deduped union ensures canUploadMore / remainingSlots are accurate
  // even in split-format sessions where each format is managed independently.
  const seenSharedIds = new Set<string>();
  const images: UploadedImage[] = [];
  for (const formatList of Object.values(imagesByFormat)) {
    for (const img of formatList) {
      if (!seenSharedIds.has(img.sharedId)) {
        seenSharedIds.add(img.sharedId);
        images.push(img);
      }
    }
  }

  /**
   * Cover-resize an image to exactly the target dimensions using a canvas.
   * Scales and center-crops the source so no dimension is smaller than the target.
   */
  const canvasCoverResize = useCallback(
    (img: HTMLImageElement, targetW: number, targetH: number, mimeType: string): Promise<Blob> =>
      new Promise((resolve, reject) => {
        const srcW = img.naturalWidth;
        const srcH = img.naturalHeight;

        // Scale factor so the image COVERS the target (never smaller)
        const scale = Math.max(targetW / srcW, targetH / srcH);
        const scaledW = srcW * scale;
        const scaledH = srcH * scale;

        // Center-crop offsets
        const offsetX = (scaledW - targetW) / 2;
        const offsetY = (scaledH - targetH) / 2;

        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas 2D context unavailable")); return; }

        ctx.drawImage(img, -offsetX, -offsetY, scaledW, scaledH);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")),
          mimeType,
          0.95
        );
      }),
    []
  );

  /**
   * Compress and resize an image for a specific format.
   * - "16:9" → landscape 1920×1080 (only if image is larger)
   * - "9:16" → portrait 1080×1920 (only if image is larger)
   * - Converts all images to JPEG for optimal compression
   * If image is smaller than target, just compress without upscaling
   */
  const compressImageForFormat = useCallback(async (file: File, format: VideoFormat): Promise<File> => {
    const isPortrait = format === "9:16";
    const targetW = isPortrait ? 1080 : 1920;
    const targetH = isPortrait ? 1920 : 1080;
    const outputMimeType = "image/jpeg";
    const outputExtension = ".jpg";

    // Load original image to check dimensions
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      const objectUrl = URL.createObjectURL(file);
      el.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(el);
      };
      el.onerror = (err) => {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      };
      el.src = objectUrl;
    });

    let processedBlob: Blob;

    // Use canvasCoverResize to ensure the exact aspect ratio
    // If image is smaller than target, we still crop to aspect ratio but avoid blurring if possible
    // However, models usually need exact resolutions, so we upscale to match targets.
    processedBlob = await canvasCoverResize(img, targetW, targetH, outputMimeType);

    // Create file with JPEG extension
    const baseFileName = file.name.replace(/\.[^/.]+$/, "");
    const jpegFileName = `${baseFileName}${outputExtension}`;
    const jpegFile = new File([processedBlob], jpegFileName, { type: outputMimeType });

    // Final compression pass
    return imageCompression(jpegFile, {
      maxWidthOrHeight: Math.max(targetW, targetH),
      useWebWorker: true,
      fileType: outputMimeType,
      initialQuality: 0.85,
    });
  }, [canvasCoverResize]);

  /**
   * Upload a single watermark image file.
   * Watermarks are uploaded as-is without format-specific processing.
   */
  const uploadWatermark = useCallback(
    async (file: File): Promise<string | null> => {
      setUploadingCount(prev => prev + 1);

      try {
        // Get auth token
        const token = await getToken();

        // Upload file through backend proxy
        const response = await fetch(`${config.apiUrl}/media/upload`, {
          method: "POST",
          body: file,
          headers: {
            "Content-Type": file.type,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const { url: publicUrl } = await response.json();
        return publicUrl;
      } catch (error) {
        handleError(error, t("errors.uploadFailed", { fileName: file.name }));
        return null;
      } finally {
        setUploadingCount(prev => prev - 1);
      }
    },
    [getToken, handleError, t]
  );

  const uploadFiles = useCallback(
    async (files: File[], formats: VideoFormat[], replaceId?: string) => {
      const isReplacing = !!replaceId;

      // Calculate how many NEW slots are available.
      // In unified mode, we use the max across formats. In split mode (single format), we check only that format.
      const firstFormat = formats[0];
      const currentSlotsUsed = (formats.length === 1 && firstFormat)
        ? imagesByFormat[firstFormat].length
        : Math.max(imagesByFormat["16:9"].length, imagesByFormat["9:16"].length);

      const availableNewSlots = Math.max(0, maxImages - currentSlotsUsed);
      
      // If we are replacing an existing slot (isReplacing = true), we only allow 
      // ONE file to replace the current one. Subsequent files are ignored.
      // If not replacing (empty slot), we allow batch uploading up to the available slots.
      const maxFilesToAccept = isReplacing ? 1 : availableNewSlots;
      
      let filesToUpload = files;

      if (files.length > maxFilesToAccept) {
        // If they pick more than allowed, we just take the first N that fit
        filesToUpload = files.slice(0, maxFilesToAccept);
        
        // Show a warning toast
        if (isReplacing) {
          toast({
            title: t("createVideo.singleFileOnly") || "Single file only",
            description: t("createVideo.singleFileReplaceWarning") || "When replacing an image, only the first selected file will be used.",
            variant: "destructive",
          });
        } else {
          toast({
            title: t("createVideo.limitReached") || "Limit reached",
            description: t("createVideo.maxImagesWarning", { count: maxImages }) || `Only ${maxImages} images are allowed. The extra files were skipped.`,
            variant: "destructive",
          });
        }
      }

      // Cap video size at 10MB
      const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10MB
      filesToUpload = filesToUpload.filter(file => {
        if (file.type.startsWith("video/") && file.size > MAX_VIDEO_SIZE) {
          toast({
            title: t("errors.videoTooLarge"),
            description: t("errors.videoSizeLimit", { size: "10MB", fileName: file.name }),
            variant: "destructive",
          });
          return false;
        }
        return true;
      });

      if (filesToUpload.length === 0) return [];

      setUploadingCount(prev => prev + 1);
      
      // Create temporary entries for immediate preview
      const tempFileInfo = filesToUpload.map((file, index) => {
        // Use the replaceId for the first file if we're in replacement mode
        const sharedId = (isReplacing && index === 0) ? replaceId : crypto.randomUUID();
        const previewUrl = URL.createObjectURL(file);
        const formatImages: Partial<Record<VideoFormat, UploadedImage>> = {};
        
        formats.forEach(format => {
          formatImages[format] = {
            id: `temp-${sharedId}-${format}`,
            sharedId,
            url: previewUrl,
            effect: defaultEffect,
            format,
            mediaType: file.type.startsWith("video/") ? "video" : "image",
            isTemp: true,
          };
        });
        
        return { sharedId, file, previewUrl, formatImages, isReplacement: isReplacing && index === 0 };
      });

      // Add temporary images to state immediately
      setImagesByFormat(prev => {
        const next = { ...prev };
        formats.forEach(format => {
          tempFileInfo.forEach(info => {
            if (info.isReplacement) {
              // Replace the existing one in state
              next[format] = next[format].map(img => 
                img.sharedId === info.sharedId ? info.formatImages[format]! : img
              );
            } else {
              // Append new one
              next[format] = [...next[format], info.formatImages[format]!];
            }
          });
        });
        return next;
      });

      try {
        // Get auth token once for the entire batch
        const token = await getToken();

        await Promise.all(tempFileInfo.map(async (info) => {
          const { sharedId, file, previewUrl } = info;
          const tempId = `temp-${Date.now()}-${Math.random()}`;

          try {
            const uploadedImages: Partial<Record<VideoFormat, UploadedImage>> = {};
            const isVideo = file.type.startsWith("video/");

            if (isVideo) {
              // Videos: upload once — the backend normalizes dimensions per format at stitch time
              const response = await fetch(`${config.apiUrl}/media/upload`, {
                method: "POST",
                body: file,
                headers: {
                  "Content-Type": file.type,
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              });

              if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
              }

              const { url: publicUrl, name } = await response.json();

              // Share the same URL across all formats
              formats.forEach(format => {
                uploadedImages[format] = {
                  id: `${name}-${format}`,
                  sharedId,
                  url: publicUrl,
                  effect: defaultEffect,
                  format,
                  mediaType: "video",
                };
              });
            } else {
              // Images: compress and upload separately per format (different crop/resize per AR)
              await Promise.all(formats.map(async (format) => {
                const compressedFile = await compressImageForFormat(file, format);
                const formatFileName = file.name.replace(/(\.[^.]+)?$/, `-${format}$1`);
                const finalFile = new File([compressedFile], formatFileName, {
                  type: compressedFile.type,
                });

                const response = await fetch(`${config.apiUrl}/media/upload`, {
                  method: "POST",
                  body: finalFile,
                  headers: {
                    "Content-Type": finalFile.type,
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                });

                if (!response.ok) {
                  throw new Error(`Upload failed: ${response.statusText}`);
                }

                const { url: publicUrl, name } = await response.json();

                uploadedImages[format] = {
                  id: `${name}-${format}`,
                  sharedId,
                  url: publicUrl,
                  effect: defaultEffect,
                  format,
                  mediaType: "image",
                };
              }));
            }

            // Replace temporary images with actual uploaded images
            setImagesByFormat(prev => {
              const next = { ...prev };
              formats.forEach(format => {
                next[format] = next[format].map(img => 
                  img.sharedId === sharedId ? uploadedImages[format]! : img
                );
              });
              return next;
            });

            // Store original file for future format syncing
            setOriginalFilesBySharedId(prev => ({
              ...prev,
              [sharedId]: { file, name: file.name }
            }));

            // Update progress
            setUploadProgress((prev) => ({ ...prev, [tempId]: 100 }));
          } catch (error) {
            handleError(error, t("errors.uploadFailed", { fileName: file.name }));
            
            // Remove the temporary image if upload failed
            setImagesByFormat(prev => {
              const next = { ...prev };
              formats.forEach(format => {
                next[format] = next[format].filter(img => img.sharedId !== sharedId);
              });
              return next;
            });
          } finally {
            // Always revoke the preview Object URL exactly once to prevent memory leaks.
            // Placed in finally to avoid double-revoke if an error occurs after the
            // revokeObjectURL call in the success path would otherwise run twice.
            URL.revokeObjectURL(previewUrl);
          }
        }));
      } catch (error) {
        // This catch handles any errors that might occur outside the individual file upload try-catch
        console.error("Batch upload failed:", error);
      } finally {
        setUploadingCount(prev => prev - 1);
        setUploadProgress({});
      }

      // We return the preferred format images from current state
      // (Simplified: returning empty array as it's not used in CreateVideoForm)
      return []; 
    },
    [getToken, imagesByFormat, maxImages, defaultEffect, handleError, t, compressImageForFormat]
  );

  /**
   * Sync existing images with a new set of formats.
   * Useful if user selects more formats after uploading images.
   */
  const syncWithFormats = useCallback(async (currentFormats: VideoFormat[]) => {
    const sharedIds = Object.keys(originalFilesBySharedId);
    if (sharedIds.length === 0) return;

    setUploadingCount(prev => prev + 1);
    const syncedImagesByFormat: Partial<FormatImages> = { "16:9": [], "9:16": [] };

    try {
      const token = await getToken();

      await Promise.all(sharedIds.map(async (sharedId) => {
        const fileData = originalFilesBySharedId[sharedId];
        if (!fileData) return;
        const { file, name: originalName } = fileData;
        
        await Promise.all(currentFormats.map(async (format) => {
          // Check if we already have this format for this sharedId
          const exists = imagesByFormat[format].some(img => img.sharedId === sharedId);
          if (exists) return;

          const isVideo = file.type.startsWith("video/");
          let renamedFile: File;
          if (isVideo) {
            // Videos bypass compression/resizing — upload as-is
            renamedFile = file;
          } else {
            const compressedFile = await compressImageForFormat(file, format);
            const formatFileName = originalName.replace(/(\.[^.]+)?$/, `-${format}$1`);
            renamedFile = new File([compressedFile], formatFileName, { type: compressedFile.type });
          }

          const response = await fetch(`${config.apiUrl}/media/upload`, {
            method: "POST",
            body: renamedFile,
            headers: {
              "Content-Type": renamedFile.type,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          if (response.ok) {
            const { url: publicUrl, name } = await response.json();
            const uploadedImage: UploadedImage = {
              id: `${name}-${format}`,
              sharedId,
              url: publicUrl,
              effect: defaultEffect,
              format,
              mediaType: file.type.startsWith("video/") ? "video" : "image",
            };
            syncedImagesByFormat[format]?.push(uploadedImage);
          }
        }));
      }));

      // Merge synced images into state only if we have changes
      const hasSyncedImages = Object.values(syncedImagesByFormat).some(imgs => imgs && imgs.length > 0);
      if (hasSyncedImages) {
        setImagesByFormat(prev => {
          const next = { ...prev };
          Object.entries(syncedImagesByFormat).forEach(([fmt, imgs]) => {
            const format = fmt as VideoFormat;
            if (imgs && imgs.length > 0) {
              // Only add if they don't already exist in state (to be safe)
              const filteredNew = imgs.filter(img => !next[format].some(e => e.sharedId === img.sharedId));
              if (filteredNew.length > 0) {
                next[format] = [...next[format], ...filteredNew];
              }
            }
          });
          return next;
        });
      }
    } catch (error) {
      handleError(error, t("errors.syncFailed"));
    } finally {
      setUploadingCount(prev => prev - 1);
    }
  }, [originalFilesBySharedId, imagesByFormat, getToken, compressImageForFormat, defaultEffect, handleError, t]);

  /**
   * Remove an image by its sharedId (removes from all formats by default).
   */
  const removeImageById = useCallback((sharedId: string, format?: VideoFormat) => {
    setImagesByFormat((prev) => {
      const next = {
        ...prev,
        ...(format
          ? { [format]: prev[format].filter((img) => img.sharedId !== sharedId) }
          : {
              "16:9": prev["16:9"].filter((img) => img.sharedId !== sharedId),
              "9:16": prev["9:16"].filter((img) => img.sharedId !== sharedId),
            }),
      };

      // Only remove from original files if it's removed from ALL formats
      const existsInAny = Object.values(next).some(list => list.some(img => img.sharedId === sharedId));
      if (!existsInAny) {
        setOriginalFilesBySharedId(prevFiles => {
          const nextFiles = { ...prevFiles };
          delete nextFiles[sharedId];
          return nextFiles;
        });
      }
      return next;
    });
  }, []);

  /**
   * Update an image by its sharedId (updates in all formats by default).
   */
  const updateImageById = useCallback(
    (sharedId: string, data: Partial<Omit<UploadedImage, "id" | "sharedId">>, format?: VideoFormat) => {
      setImagesByFormat((prev) => ({
        "16:9": (format === undefined || format === "16:9")
          ? prev["16:9"].map((img) => (img.sharedId === sharedId ? { ...img, ...data } : img))
          : prev["16:9"],
        "9:16": (format === undefined || format === "9:16")
          ? prev["9:16"].map((img) => (img.sharedId === sharedId ? { ...img, ...data } : img))
          : prev["9:16"],
      }));
    },
    []
  );

  /**
   * Reorder images by moving from one index to another.
   * Compatible with dnd-kit's arrayMove.
   */
  const reorderImages = useCallback((fromIndex: number, toIndex: number, format?: VideoFormat) => {
    setImagesByFormat((prev) => {
      const result: FormatImages = { ...prev };
      const formatsToReorder = format ? [format] : (Object.keys(result) as VideoFormat[]);
      
      formatsToReorder.forEach((f) => {
        if (result[f].length > 0) {
          const formatImages = [...result[f]];
          const [removed] = formatImages.splice(fromIndex, 1);
          if (removed) {
            formatImages.splice(toIndex, 0, removed);
          }
          result[f] = formatImages;
        }
      });
      
      return result;
    });
  }, []);

  /**
   * Clear all images.
   */
  const clearImages = useCallback(() => {
    setImagesByFormat({
      "16:9": [],
      "9:16": [],
    });
    setOriginalFilesBySharedId({});
  }, []);

  /**
   * Resets all formats to match the source format's asset list.
   */
  const unifyImages = useCallback((sourceFormat: VideoFormat) => {
    setImagesByFormat(prev => {
      const sourceAssets = prev[sourceFormat];
      const next = { ...prev };
      Object.keys(next).forEach(fmt => {
        const format = fmt as VideoFormat;
        if (format !== sourceFormat) {
          next[format] = sourceAssets.map(img => ({
            ...img,
            id: `${img.sharedId}-${format}`,
            format
          }));
        }
      });
      return next;
    });
  }, []);

  /**
   * Get images for a specific format.
   */
  const getImagesForFormat = useCallback((format: VideoFormat): UploadedImage[] => {
    return imagesByFormat[format];
  }, [imagesByFormat]);

  return {
    images,
    imagesByFormat,
    isUploading,
    uploadProgress,
    uploadFiles,
    uploadWatermark,
    removeImageById,
    updateImageById,
    reorderImages,
    clearImages,
    unifyImages,
    getImagesForFormat,
    syncWithFormats,
    canUploadMore: Math.max(imagesByFormat["16:9"].length, imagesByFormat["9:16"].length) < maxImages,
    remainingSlots: maxImages - Math.max(imagesByFormat["16:9"].length, imagesByFormat["9:16"].length),
  };
}
